import { forwardRef } from 'react';
import { Pencil } from 'lucide-react';
import { linkify } from '../lib/linkify.js';
import { Button } from '../ui/Button.js';
import { cn } from '../lib/cn.js';

/**
 * Un texte libre rendu avec ses liens cliquables (`lib/linkify.ts`) : liens markdown et URL
 * nues. Les sauts de ligne sont gardés. Un clic sur un lien ne remonte pas au conteneur (une
 * ligne de liste ouvre la tâche au clic, un aperçu bascule en édition).
 */
export function TexteLie({ text }: { text: string }): React.ReactElement {
  return (
    <>
      {linkify(text).map((m, i) =>
        m.kind === 'texte' ? (
          m.text
        ) : (
          <a
            key={i}
            href={m.href}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {m.label}
          </a>
        )
      )}
    </>
  );
}

/** Vrai si le texte porte au moins un lien que `TexteLie` rendrait cliquable. */
export function aDesLiens(text: string): boolean {
  return linkify(text).some((m) => m.kind === 'lien');
}

/**
 * Le mode lecture d'un champ texte : son rendu lié, et un crayon. Un clic sur le texte (hors
 * lien, hors sélection) ou sur le crayon demande l'édition ; le crayon reçoit le `ref`, pour
 * qu'on lui rende le focus en sortant de l'édition par Escape. `minHeight` : la hauteur de la
 * saisie qu'il remplace, pour que le passage édition → lecture ne décale rien sous le pointeur.
 */
export const ApercuLie = forwardRef<
  HTMLButtonElement,
  { text: string; onEdit: () => void; editLabel: string; className?: string; minHeight?: number | undefined }
>(function ApercuLie({ text, onEdit, editLabel, className, minHeight }, ref) {
  return (
    <div className="flex items-start gap-2">
      <div
        className={cn('saisie py-2 flex-1 min-w-0 break-words cursor-text', className)}
        style={minHeight ? { minHeight } : undefined}
        onClick={() => {
          // Sélectionner un bout du texte pour le copier n'est pas demander l'édition.
          if (window.getSelection()?.toString()) return;
          onEdit();
        }}
      >
        <TexteLie text={text} />
      </div>
      <Button ref={ref} variant="quiet" size="icon-small" onClick={onEdit} aria-label={editLabel}>
        <Pencil size={14} aria-hidden />
      </Button>
    </div>
  );
});
